import * as THREE from 'three';
import { AssetLoader } from '../loading/AssetLoader';
import { TILE_SIZE, GRID_SIZE, KENNEY_SCALE } from '../config';
import { carModels } from '../loading/AssetManifest';
import { createSeededRandom } from '../utils/math';

export interface CarInstance {
  model: THREE.Group;
  carName: string;
  isRow: boolean;       // true = driving along X, false = along Z
  roadIndex: number;    // grid index of the road corridor
  lane: number;         // offset from road center
  speed: number;
  progress: number;     // position along driving axis
  direction: 1 | -1;
  lastTurnPos: number;  // last intersection where we made a turn decision
  controlledByPlayer?: boolean;
  /** A floating beacon attached as a child of model when highlighted.
   * Null when the taxi is not being highlighted. Previously we mutated
   * the car's own emissive, which washed out the paint. */
  _beacon?: THREE.Group;
  _beaconPhase?: number;
}

const CAR_COUNT = 150;
const CAR_SPEED_MIN = 3;
const CAR_SPEED_MAX = 6;
const CAR_SCALE = KENNEY_SCALE * 0.25;
const ROAD_INDICES: number[] = (() => {
  const arr: number[] = [];
  for (let i = 0; i < GRID_SIZE; i += 3) arr.push(i);
  return arr;
})();
const TURN_CHANCE = 0.4;

// Precompute world positions for each road corridor
const HALF = (GRID_SIZE - 1) / 2;
const ROAD_WORLD_POS = ROAD_INDICES.map(idx => (idx - HALF) * TILE_SIZE);
const CITY_MIN = ROAD_WORLD_POS[0];
const CITY_MAX = ROAD_WORLD_POS[ROAD_WORLD_POS.length - 1];

// Reverse lookup: world position → road grid index
const WORLD_TO_ROAD = new Map<number, number>();
ROAD_INDICES.forEach((idx, i) => WORLD_TO_ROAD.set(ROAD_WORLD_POS[i], idx));

export class CarTraffic {
  private loader: AssetLoader;
  private cars: CarInstance[] = [];
  private group = new THREE.Group();
  private rng = createSeededRandom(789);

  constructor(scene: THREE.Scene, loader: AssetLoader) {
    this.loader = loader;
    scene.add(this.group);
  }

  spawn() {
    for (let i = 0; i < CAR_COUNT; i++) {
      const carName = carModels[Math.floor(this.rng() * carModels.length)];
      const key = `car/${carName}`;
      const model = this.loader.getModel(key);
      if (!model) continue;

      model.scale.setScalar(CAR_SCALE);
      model.traverse((c) => {
        if (c instanceof THREE.Mesh) c.castShadow = true;
      });

      const isRow = this.rng() > 0.5;
      const roadIndex = ROAD_INDICES[Math.floor(this.rng() * ROAD_INDICES.length)];
      const direction: 1 | -1 = this.rng() > 0.5 ? 1 : -1;
      const lane = direction * 0.6;
      const speed = CAR_SPEED_MIN + this.rng() * (CAR_SPEED_MAX - CAR_SPEED_MIN);
      // Start at a random intersection along the corridor
      const startPos = ROAD_WORLD_POS[Math.floor(this.rng() * ROAD_WORLD_POS.length)];

      const car: CarInstance = {
        model, carName, isRow, roadIndex, lane, speed,
        progress: startPos,
        direction,
        lastTurnPos: startPos,
      };

      this.positionCar(car);
      this.group.add(model);
      this.cars.push(car);
    }
  }

  update(delta: number) {
    for (const car of this.cars) {
      // Beacons animate even if the car is not moving (parked taxis never
      // carry one, so this only runs on highlighted cars that are moving).
      if (car._beacon && car._beaconPhase != null) {
        car._beaconPhase += delta * 3;
        car._beacon.position.y = 2.5 + Math.sin(car._beaconPhase) * 0.18;
        car._beacon.rotation.y += delta * 2;
      }
      if (car.controlledByPlayer) continue;
      const prev = car.progress;
      car.progress += car.speed * car.direction * delta;

      // Check if we crossed any intersection this frame
      for (const intPos of ROAD_WORLD_POS) {
        if (intPos === car.lastTurnPos) continue;

        const crossed =
          (car.direction > 0 && prev < intPos && car.progress >= intPos) ||
          (car.direction < 0 && prev > intPos && car.progress <= intPos);
        if (!crossed) continue;

        // At city edge → must turn. Otherwise random chance.
        const isEdge = intPos === CITY_MIN || intPos === CITY_MAX;
        if (isEdge || this.rng() < TURN_CHANCE) {
          this.turnCar(car, intPos);
        } else {
          car.lastTurnPos = intPos;
        }
        break; // one intersection per frame
      }

      // Safety: clamp if somehow past city bounds
      if (car.progress > CITY_MAX + 2 || car.progress < CITY_MIN - 2) {
        const nearest = car.progress > 0 ? CITY_MAX : CITY_MIN;
        this.turnCar(car, nearest);
      }

      this.positionCar(car);
    }
  }

  private turnCar(car: CarInstance, intPos: number) {
    const newRoadIdx = WORLD_TO_ROAD.get(intPos);
    if (newRoadIdx == null) return;

    // Old fixed-axis position becomes new progress
    const oldFixedPos = (car.roadIndex - HALF) * TILE_SIZE;

    car.progress = oldFixedPos;
    car.roadIndex = newRoadIdx;
    car.isRow = !car.isRow;
    car.direction = this.rng() > 0.5 ? 1 : -1;
    car.lane = car.direction * 0.6;
    car.lastTurnPos = oldFixedPos;
  }

  findNearestCar(x: number, z: number, maxDist: number): CarInstance | null {
    let best: CarInstance | null = null;
    let bestDist = maxDist;
    for (const car of this.cars) {
      if (car.controlledByPlayer) continue;
      const dx = car.model.position.x - x;
      const dz = car.model.position.z - z;
      const d = Math.sqrt(dx * dx + dz * dz);
      if (d < bestDist) {
        bestDist = d;
        best = car;
      }
    }
    return best;
  }

  markAsParked(car: CarInstance) {
    car.controlledByPlayer = true;
  }

  findNearestTaxi(x: number, z: number, maxDist: number): CarInstance | null {
    let best: CarInstance | null = null;
    let bestDist = maxDist;
    for (const car of this.cars) {
      if (car.carName !== 'taxi' || car.controlledByPlayer) continue;
      const dx = car.model.position.x - x;
      const dz = car.model.position.z - z;
      const d = Math.hypot(dx, dz);
      if (d < bestDist) {
        bestDist = d;
        best = car;
      }
    }
    return best;
  }

  findNearestTaxis(x: number, z: number, count: number): CarInstance[] {
    const ranked: Array<{ car: CarInstance; d: number }> = [];
    for (const car of this.cars) {
      if (car.carName !== 'taxi' || car.controlledByPlayer) continue;
      const d = Math.hypot(car.model.position.x - x, car.model.position.z - z);
      ranked.push({ car, d });
    }
    ranked.sort((a, b) => a.d - b.d);
    return ranked.slice(0, count).map((r) => r.car);
  }

  applyTaxiHighlight(car: CarInstance, enabled: boolean) {
    if (enabled) {
      if (car._beacon) return;
      const beacon = new THREE.Group();
      const coneGeom = new THREE.ConeGeometry(0.28, 0.55, 4);
      const coneMat = new THREE.MeshBasicMaterial({
        color: 0xffcc33,
        transparent: true,
        opacity: 0.95,
        depthTest: false,
      });
      const cone = new THREE.Mesh(coneGeom, coneMat);
      cone.rotation.x = Math.PI; // apex points down at the taxi
      cone.renderOrder = 9999;
      beacon.add(cone);
      // A soft halo disc behind the cone so it reads at distance.
      const haloGeom = new THREE.RingGeometry(0.35, 0.55, 24);
      const haloMat = new THREE.MeshBasicMaterial({
        color: 0xffcc33,
        transparent: true,
        opacity: 0.55,
        side: THREE.DoubleSide,
        depthTest: false,
        blending: THREE.AdditiveBlending,
      });
      const halo = new THREE.Mesh(haloGeom, haloMat);
      halo.renderOrder = 9999;
      beacon.add(halo);
      // Sits ~2.5 units above the car's local origin.
      beacon.position.y = 2.5;
      car.model.add(beacon);
      car._beacon = beacon;
      car._beaconPhase = Math.random() * Math.PI * 2;
    } else {
      if (!car._beacon) return;
      const beacon = car._beacon;
      car.model.remove(beacon);
      beacon.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          const mat = obj.material;
          if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
          else if (mat) mat.dispose();
        }
      });
      car._beacon = undefined;
      car._beaconPhase = undefined;
    }
  }

  releaseTaxi(car: CarInstance) {
    car.controlledByPlayer = false;
  }

  private positionCar(car: CarInstance) {
    const roadWorld = (car.roadIndex - HALF) * TILE_SIZE;

    if (car.isRow) {
      // Driving along X, fixed Z
      car.model.position.set(car.progress, 0.05, roadWorld + car.lane);
      car.model.rotation.y = car.direction > 0 ? Math.PI / 2 : -Math.PI / 2;
    } else {
      // Driving along Z, fixed X
      car.model.position.set(roadWorld + car.lane, 0.05, car.progress);
      car.model.rotation.y = car.direction > 0 ? 0 : Math.PI;
    }
  }
}
